import { useState } from 'react';
import { api, setCsrfToken, type Session } from '../lib/api.js';

/**
 * The throwaway admin that `scripts/run-local.mjs` seeds into apnakosh_local.
 *
 * Pre-filled ONLY when the page is being served from loopback, so it can never
 * appear on apnakosh.in. Two reasons that gate is safe rather than merely
 * convenient: this account exists only in a database created on a developer's
 * own machine, and its credentials are already written in plain sight in
 * run-local.mjs. Nothing secret is being moved into the bundle.
 *
 * It is also why the seeded password is deliberately worthless rather than
 * something memorable-and-reused: a convenience default must never be a
 * credential anyone would be tempted to use twice.
 */
const LOCAL_SEED = { email: 'local@apnakosh.test', password: 'a-local-passphrase-99' };

function isLoopback(): boolean {
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname;
  return host === 'localhost' || host === '127.0.0.1' || host === '[::1]' || host === '::1';
}

export function Login({ onSignedIn }: { onSignedIn: (s: Session) => void }) {
  const local = isLoopback();
  const [email, setEmail] = useState(local ? LOCAL_SEED.email : '');
  const [password, setPassword] = useState(local ? LOCAL_SEED.password : '');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const session = await api.post<Session & { csrfToken: string }>('/auth/login', { email, password });
      setCsrfToken(session.csrfToken);
      onSignedIn(session);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <main
      style={{
        minHeight: '100dvh',
        display: 'grid',
        placeItems: 'center',
        padding: 'var(--gutter)',
        background: 'var(--color-bg-base)',
      }}
    >
      <form onSubmit={submit} className="card" style={{ width: '100%', maxWidth: 380, padding: 'var(--space-6)' }}>
        <h1 className="page-title" style={{ textAlign: 'center' }}>
          ApnaKosh
        </h1>
        <p className="page-sub" style={{ textAlign: 'center', marginBottom: local ? 'var(--space-4)' : 'var(--space-6)' }}>
          The family wealth OS
        </p>

        {/* Unmistakable: a local instance holds test data and must never be
            confused with the real one when both are open in the same browser. */}
        {local && (
          <p
            className="state-warn"
            style={{
              font: 'var(--type-caption)',
              textAlign: 'center',
              marginBottom: 'var(--space-4)',
              border: '1px dashed var(--color-status-warning)',
              borderRadius: 'var(--radius-chip)',
              padding: 'var(--space-2)',
            }}
          >
            local instance · seeded sign-in filled in
          </p>
        )}

        <div style={{ display: 'grid', gap: 'var(--space-3)', marginBottom: 'var(--space-4)' }}>
          <label className="field">
            Email
            <input type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </label>
          <label className="field">
            Password
            <input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </label>
        </div>

        {error && (
          <p role="alert" className="state-bad" style={{ font: 'var(--type-footnote)' }}>
            {error}
          </p>
        )}

        {/* The one filled button (Law 1), full-width in the thumb zone. */}
        <button type="submit" className="btn btn-primary" disabled={busy} style={{ width: '100%', minHeight: 50 }}>
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
    </main>
  );
}
